import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Paper, Typography } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';

const RMSFileUpload = ({
  files = [],
  onChange,
  multiple = true,
  accept = '*',
  maxFiles = 20,
  maxFileSize = 100,
  allowDuplicates = false,
  disabled = false,
  readOnly = false,
  showEmptyState = true,
  title = 'Drop files here or click to browse',
  subtitle = 'Supported file types',
  emptyText = 'No attachments yet. Drop files above or click to browse.',
  onValidationError,
  className = '',
}) => {
  const inputRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [validationMessages, setValidationMessages] = useState([]);

  const acceptedTypes = useMemo(() => {
    return accept === '*' ? [] : accept.split(',').map((x) => x.trim().toLowerCase());
  }, [accept]);

  useEffect(() => {
    if (!validationMessages.length) return;
    const timer = setTimeout(() => {
      setValidationMessages([]);
    }, 5000);
    return () => clearTimeout(timer);
  }, [validationMessages]);

  const isValidType = useCallback(
    (file) => {
      if (accept === '*') return true;
      return acceptedTypes.some((type) => {
        if (type.startsWith('.')) {
          return file.name.toLowerCase().endsWith(type);
        }
        if (type.endsWith('/*')) {
          return file.type.startsWith(type.replace('*', ''));
        }
        return file.type === type;
      });
    },
    [accept, acceptedTypes]
  );

  const handleFileSelect = useCallback(
    (selectedFiles) => {
      if (disabled || readOnly) return;

      setValidationMessages([]);
      const selectedArray = Array.from(selectedFiles);

      if (maxFiles && files.length + selectedArray.length > maxFiles) {
        const error = `You can upload up to ${maxFiles} files. Please remove some files and try again.`;
        setValidationMessages([error]);
        onValidationError?.([error]);
        return;
      }

      const validFiles = [];
      const errors = [];
      const oversizedFiles = [];
      const duplicateFiles = [];
      const invalidTypeFiles = [];

      selectedArray.forEach((file) => {
        const duplicateExists =
          !allowDuplicates &&
          files.some(
            (existing) =>
              existing.file.name === file.name &&
              existing.file.size === file.size &&
              existing.file.lastModified === file.lastModified
          );
        if (duplicateExists) {
          duplicateFiles.push(file.name);
          return;
        }
        if (maxFileSize && file.size > maxFileSize * 1024 * 1024) {
          oversizedFiles.push(file.name);
          return;
        }
        if (!isValidType(file)) {
          invalidTypeFiles.push(file.name);
          return;
        }
        validFiles.push({
          id: `${file.name}_${file.size}_${file.lastModified}`,
          file,
          title: file.name.replace(/\.[^/.]+$/, ''),
        });
      });

      if (validFiles.length) {
        onChange([...files, ...validFiles]);
      }

      if (oversizedFiles.length) {
        errors.push(
          `These files exceed the ${maxFileSize} MB limit and were not uploaded: ${oversizedFiles.join(
            ', '
          )}`
        );
      }

      if (invalidTypeFiles.length) {
        errors.push(
          `These file types are not supported and were not uploaded: ${invalidTypeFiles.join(
            ', '
          )}.`
        );
      }

      if (duplicateFiles.length) {
        errors.push(
          `These files were already selected and were skipped: ${duplicateFiles.join(', ')}.`
        );
      }

      if (errors.length) {
        setValidationMessages(errors);
        onValidationError?.(errors);
      }
    },
    [
      disabled,
      readOnly,
      maxFiles,
      files,
      maxFileSize,
      allowDuplicates,
      isValidType,
      onChange,
      onValidationError,
    ]
  );

  const handleBrowse = useCallback(() => {
    if (!disabled && !readOnly) {
      inputRef.current?.click();
    }
  }, [disabled, readOnly]);

  const handleInputChange = useCallback(
    (event) => {
      handleFileSelect(event.target.files);
      event.target.value = '';
    },
    [handleFileSelect]
  );

  const handleDragEnter = useCallback(
    (e) => {
      e.preventDefault();
      if (!disabled && !readOnly) {
        setIsDragging(true);
      }
    },
    [disabled, readOnly]
  );

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setIsDragging(false);
    }
  }, []);

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      setIsDragging(false);
      handleFileSelect(e.dataTransfer.files);
    },
    [handleFileSelect]
  );

  return (
    <Box className={`rms-file-upload ${className}`}>
      {!readOnly && (
        <Paper
          variant="outlined"
          className={`upload-drop-zone ${isDragging ? 'drag-active' : ''} ${
            disabled ? 'disabled' : ''
          }`}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={(e) => {
            e.preventDefault();
            if (!disabled && !readOnly) {
              setIsDragging(true);
            }
          }}
          onDrop={handleDrop}
          onClick={handleBrowse}>
          <AddIcon className="upload-icon" />
          <Typography className="upload-title"> {title} </Typography>
          <Typography className="upload-subtitle"> {subtitle} </Typography>
          <input
            hidden
            ref={inputRef}
            type="file"
            multiple={multiple}
            accept={accept}
            disabled={disabled}
            onChange={handleInputChange}
          />
        </Paper>
      )}
      {validationMessages.length > 0 && (
        <Box py={0.5}>
          {validationMessages.map((message, index) => (
            <Typography key={index} className="file-upload-error">
              {message}
            </Typography>
          ))}
        </Box>
      )}

      {files.length === 0 && showEmptyState && (
        <Box className="empty-state">
          <Typography className="empty-text">{emptyText}</Typography>
        </Box>
      )}
    </Box>
  );
};
export default React.memo(RMSFileUpload);
